import { matchPath } from 'react-router';

import { takeLatest, put, call, all, select, take } from 'redux-saga/effects';

import { restClient } from 'config/store/configureStore';
import * as types from 'constants/ActionTypes';
import { PATH } from 'constants/RoutesConstants';
import {
  STATS_PATHS_DETAILS,
  STATS_PATHS_DASHBOARD,
  SELF_SERVICE_STATS_TABS,
  ALL_STATS_PATHS_BY_MODE,
} from 'constants/StatisticsConstants';
import { PRIVATE, PUBLIC } from 'constants/VisibilityConstants';
import { APPLICATION_IS_READY } from 'modules/app/actionTypes';
import { fetchPage } from 'modules/app/sagas/resolveAll';
import { applicationIsReady } from 'modules/app/selectors';
import { searchExtras } from 'modules/checklists/actions/dashboard';
import { SEARCH_CHECKLISTS_FAILURE, SEARCH_CHECKLISTS_SUCCESS } from 'modules/checklists/actionTypes';
import { getParams as getExtraParams } from 'modules/checklists/selectors/extrasStat';
import { selectDealer } from 'modules/dealers/actions';
import { SELECT_GROUP } from 'modules/groups/actionTypes';
import { getSelectedGroupId } from 'modules/groups/selectors';
import * as actions from 'modules/questionModels/actions';
import { fetchQuestionsDetails } from 'modules/questionModels/sagas';
import { getParams } from 'modules/questionModels/selectors/ui/select';
import { LOCATION_CHANGE } from 'modules/router/actionTypes';
import {
  getLocation,
  isSelfServiceStats,
  isSelfServiceStatsDetails,
  isFirstRendering as isFirstRenderingSelector,
} from 'modules/router/selectors';
import {
  isInit,
  getExtraFilters,
  getSelfServiceTab,
  getDealersStatsParams,
  getExtraDetailsParams,
  getAllDealersStatsParams,
  getProactiveWorksDetailsParams,
  getSelfServiceDashboardStatsWTotal,
  getSelfServiceStatsDetailsFetchParams,
  getSelfServiceTopContributorsParams,
} from 'modules/statistics/selectors';
import { createNotification } from 'modules/toast-notifications/actions';
import { getCurrentUserIsManufacturer } from 'modules/users/selectors/currentUser';

import { getSelectedDealerId } from 'selectors/dealerSelectors';

import { uniq } from 'utils/ObjectUtils';

import * as dealerStatisticsActions from './actions';
import * as actionTypes from './actionTypes';

const checkResponse = data => {
  if (data.page.totalElements === 0) {
    return {
      ...data,
      content: [],
    };
  }
  return data;
};

export function* fetchReduce(request) {
  const hasModules = request.params?.modules?.length > 0;
  if (hasModules) {
    return yield* fetchPage(request);
  }
  return {
    content: [],
    page: {
      size: request?.params?.size,
      number: 0,
      totalElements: 0,
      totalPages: 0,
    },
    links: [],
  };
}

/**
 * @param {import('./types').FetchDealersStatsRequestAction} action
 */
function* fetchDealersStats({ payload }) {
  const { request } = payload;
  try {
    const { data } = yield call(restClient.request, request);
    yield put(dealerStatisticsActions.fetchDealersStatsSuccess(data));
  } catch (e) {
    yield put(dealerStatisticsActions.fetchDealersStatsFailure(e));
  }
}

/**
 * @param {import('./types').FetchAllDealersStatsRequestAction} action
 */
function* fetchAllDealersStats({ payload }) {
  const { request } = payload;
  try {
    const response = yield call(restClient.request, request);
    yield put(dealerStatisticsActions.fetchAllDealersStatsSuccess(response.data));
  } catch (e) {
    yield put(dealerStatisticsActions.fetchAllDealersStatsFailure(e));
  }
}

/**
 * @param {import('./types').FetchDealersCountriesRequestAction} action
 */
function* fetchCountriesStats({ payload }) {
  const { request } = payload;
  try {
    const { data } = yield call(restClient.request, request);
    yield put(dealerStatisticsActions.fetchDealersCountriesSuccess(data));
  } catch (e) {
    yield put(dealerStatisticsActions.fetchDealersCountriesFailure(e));
  }
}

/**
 * @param {import('./types').FetchDealersStatsBrandsRequestAction} action
 */
function* fetchCurrentStatsBrands({ payload }) {
  const { request } = payload;
  try {
    const data = yield fetchPage(request);
    yield put(dealerStatisticsActions.fetchDealersStatsBrandsSuccess(data?.content ?? []));
  } catch (e) {
    yield put(dealerStatisticsActions.fetchDealersStatsBrandsFailure(e));
  }
}

/**
 * @param {import('./types').FetchInterventionStatusStatsRequestAction} action
 */
function* fetchCurrentStatusStats({ payload }) {
  const { request } = payload;
  try {
    const data = yield fetchReduce(request);
    yield put(dealerStatisticsActions.fetchInterventionStatusStatsSuccess(data));
  } catch (error) {
    yield put(dealerStatisticsActions.fetchInterventionStatusStatsFailure(error));
  }
}

/**
 * @param {import('./types').FetchInterventionRatingStatsRequestAction} action
 */
function* fetchInterventionRatingStats({ payload }) {
  const { request } = payload;
  try {
    const data = yield fetchReduce(request);
    yield put(dealerStatisticsActions.fetchInterventionRatingStatsSuccess(data));
  } catch (error) {
    yield put(dealerStatisticsActions.fetchInterventionRatingStatsFailure(error));
  }
}

/**
 * @param {import('./types').FetchTimeLineStatusStatsRequestAction} action
 */
function* fetchStatusStats({ payload }) {
  const { request } = payload;
  try {
    const data = yield fetchReduce(request);
    yield put(dealerStatisticsActions.fetchTimeLineStatusStatsSuccess(checkResponse(data)));
  } catch (error) {
    yield put(dealerStatisticsActions.fetchTimeLineStatusStatsFailure(error));
  }
}

/**
 * @param {import('./types').FetchCustomerOpinionStatsRequestAction} action
 */
function* fetchCustomerOpinionStats({ payload }) {
  const { request } = payload;
  try {
    const data = yield fetchReduce(request);
    yield put(dealerStatisticsActions.fetchCustomerOpinionStatsSuccess(data));
  } catch (e) {
    yield put(dealerStatisticsActions.fetchCustomerOpinionStatsFailure(e));
  }
}

/**
 * @param {import('./types').FetchTopContributorsStatsRequestAction} action
 */
function* fetchTopContributorsStats({ payload }) {
  const { request } = payload;
  try {
    const data = yield fetchReduce(request);
    yield put(dealerStatisticsActions.fetchTopContributorsStatsSuccess(data));
  } catch (error) {
    yield put(dealerStatisticsActions.fetchTopContributorsStatsFailure(error));
  }
}

/**
 * @param {import('./types').FetchAnswerTimeRequestAction} action
 */
function* fetchAnswerTime({ payload }) {
  const { request } = payload;
  try {
    const data = yield fetchReduce(request);
    yield put(dealerStatisticsActions.fetchAnswerTimeSuccess(data));
  } catch (e) {
    yield put(dealerStatisticsActions.fetchAnswerTimeFailure(e));
  }
}

/**
 * @param {import('./types').FetchInterventionCountRequestAction} action
 */
function* fetchInterventionCount({ payload }) {
  const { request } = payload;
  try {
    const data = yield fetchReduce(request);
    const { id: count, checklistProposed: workCount } = data.content?.[0] ?? {};
    yield put(
      dealerStatisticsActions.fetchInterventionCountSuccess({
        count,
        workCount,
      })
    );
  } catch (e) {
    yield put(dealerStatisticsActions.fetchInterventionCountFailure(e));
  }
}

/**
 * @param {import('./types').FetchRevenueStatsRequestAction} action
 */
function* fetchRevenueStats({ payload }) {
  const { requests } = payload;
  try {
    const [details, average, revenue] = yield all(requests?.map(fetchReduce));

    const answeredRevenue = revenue?.content?.find(d => d.answered === 1);
    yield put(
      dealerStatisticsActions.fetchRevenueStatsSuccess({
        ...details.content?.[0],
        acceptedAvg: average.content?.[0]?.accepted,
        acceptedAmount: answeredRevenue?.accepted ?? 0,
        rejectedAmount: (answeredRevenue?.proposed ?? 0) - (answeredRevenue?.accepted ?? 0),
      })
    );
  } catch (e) {
    yield put(dealerStatisticsActions.fetchRevenueStatsFailure(e));
  }
}

/**
 * @param {import('./types').FetchRevenueByBrandStatsRequestAction} action
 */
function* fetchRevenueByBrandStats({ payload }) {
  const { request } = payload;
  try {
    const data = yield fetchReduce(request);
    yield put(dealerStatisticsActions.fetchRevenueByBrandStatsSuccess(data));
  } catch (e) {
    yield put(dealerStatisticsActions.fetchRevenueByBrandStatsFailure(e));
  }
}

/**
 * @param {import('./types').FetchRevenueByPeriodStatsRequestAction} action
 */
function* fetchRevenueByPeriodStats({ payload }) {
  const { request } = payload;
  try {
    const data = yield fetchReduce(request);
    yield put(dealerStatisticsActions.fetchRevenueByPeriodStatsSuccess(checkResponse(data)));
  } catch (e) {
    yield put(dealerStatisticsActions.fetchRevenueByPeriodStatsFailure(e));
  }
}

/**
 * @param {import('modules/global/generic.types').LocationChangeAction} action
 */
function* startDashboardApplication({ payload }) {
  const { pathname: location } = payload.location;
  const isRightLocation =
    matchPath(PATH.STATS_ODITOO.simplePath, location) !== null ||
    Object.values(ALL_STATS_PATHS_BY_MODE)
      .flatMap(paths => paths)
      .some(path => matchPath(path.path, location) !== null);

  if (isRightLocation) {
    const isReady = yield select(applicationIsReady);
    if (!isReady) {
      yield take(APPLICATION_IS_READY);
    }

    yield put(dealerStatisticsActions.fetchRefreshTime());
  }
}

/**
 * @param {import('./types').FetchRefreshTimeAction} action
 */
function* fetchLatestRefreshTime({ payload, meta }) {
  const { request } = payload;
  try {
    const result = yield call(restClient.request, request);
    yield put(dealerStatisticsActions.fetchRefreshTimeSuccess(result.data, meta));
  } catch (e) {
    yield put(dealerStatisticsActions.fetchRefreshTimeFailure(e));
  }
}

function* afterSetFiltersWorker(action = {}) {
  const isSelfServiceStatsLocation = yield select(isSelfServiceStats);

  if (!isSelfServiceStatsLocation) {
    const waitSelectDealer =
      action.type === SELECT_GROUP ||
      (action.type === types.SET_DEALER_STATISTICS_FILTERS && action.payload.countryCodes?.length > 0);
    if (waitSelectDealer) {
      yield take(types.SELECT_DEALER);
    } else {
      const dealersParams = yield select(getDealersStatsParams);
      const allParams = yield select(getAllDealersStatsParams);

      const { dealerId: _dealerId, ...allParamsWithoutDealerId } = allParams;

      yield all([
        put(dealerStatisticsActions.fetchDealersStatsRequest(dealersParams)),
        put(dealerStatisticsActions.fetchAllDealersStatsRequest(allParamsWithoutDealerId)),
        put(dealerStatisticsActions.fetchDealersCountriesRequest(allParams)),
      ]);
    }
  }
}

/**
 * @param {string} pathname
 * @returns {boolean}
 */
const isStatisticDashboard = pathname => {
  const pages = Object.values(STATS_PATHS_DASHBOARD).reduce((acc, paths) => acc.concat(paths), []);
  return pages.find(path => matchPath(path, pathname));
};

/**
 * @param {string} pathname
 * @returns {boolean}
 */
const isStatisticDetails = pathname => {
  const pages = Object.values(STATS_PATHS_DETAILS).reduce((acc, paths) => acc.concat(paths), []);
  return pages.find(path => matchPath(path, pathname));
};

/**
 * @param {import('modules/global/generic.types').LocationChangeAction} action
 */
function* initStatisticsDashboard({ payload }) {
  const isMatching = isStatisticDashboard(payload.location.pathname);

  if (isMatching) {
    const isFirstRendering = yield select(isFirstRenderingSelector);
    if (isFirstRendering) {
      yield take(APPLICATION_IS_READY);
    }
    yield call(afterSetFiltersWorker);
  }
}

/**
 * @param {import('modules/global/generic.types').LocationChangeAction} action
 */
function* initStatisticsDetails({ payload }) {
  const match = isStatisticDetails(payload.location.pathname);
  if (match) {
    const isFirstRendering = yield select(isFirstRenderingSelector);
    if (isFirstRendering) {
      yield take(APPLICATION_IS_READY);
    }

    const selectedDealerId = yield select(getSelectedDealerId);
    const tmpDealerId = payload.location.pathname.split('/').pop();
    const dealerId = tmpDealerId !== 'all' ? tmpDealerId : '';

    if (dealerId !== selectedDealerId) {
      yield put(selectDealer(dealerId));
    }

    const isManufacturer = yield select(getCurrentUserIsManufacturer);
    if (!isManufacturer && dealerId) {
      const groupId = yield select(getSelectedGroupId);
      if (!groupId) {
        yield take(SELECT_GROUP);
      }
    }

    yield put(dealerStatisticsActions.detailsIsReady());
  }
}

function* onSelectedDealerChange() {
  const location = yield select(getLocation);
  const isMatching = isStatisticDashboard(location.pathname);

  if (isMatching) {
    yield call(afterSetFiltersWorker);
  }
}

/**
 * @param {import('./types').ExportStatisticsAction} action
 */
function* generateExportData({ payload, meta }) {
  const { request } = payload;
  try {
    yield call(restClient.request, request);
    yield put(dealerStatisticsActions.exportStatisticsSuccess(meta));
    yield put(
      createNotification({
        type: 'success',
        message: {
          id: 'statistics_intervention.export_success',
          defaultMessage: 'Export in progress. Please check your inbox in a few minutes.',
        },
      })
    );
  } catch (error) {
    yield put(dealerStatisticsActions.exportStatisticsFailure(error, meta));
    if (
      error?.response?.data.errorCode === 409 &&
      error.response.data.errorType === 'SAME_USER_EXPORT_ALREADY_EXISTS'
    ) {
      yield put(
        createNotification({
          type: 'simple-error',
          message: {
            id: 'statistics_identical.content',
            defaultMessage: 'Your export is being processed. This operation may take up to an hour.',
          },
        })
      );
    } else {
      yield put(
        createNotification({
          type: 'error',
          message: {
            id: 'statistics_intervention.export_error',
            defaultMessage: 'An error occurred during the export of the statistics.',
          },
        })
      );
    }
  }
}

/** @param {import('./types').FetchChatStatisticsRequestAction} action */
function* fetchChatStatistics({ payload }) {
  const { requests } = payload;

  try {
    const [
      messagesByInterventionResponse,
      channelResponse,
      messagesDistributionResponse,
      messagesCountsResponse,
      averageTimeToAnswerResponse,
      internalMessagesResponse,
      nbMediaFilesSharedWithCustomersResponse,
      nbMediaFileSharedInternallyResponse,
      averageNbMediaFilesSharedInChatPerFileResponse,
      nbMediaFilesSentToCustomersResponse,
      nbMediaFilesReceivedToCustomersResponse,
    ] = yield all(requests.map(request => call(fetchPage, request)));

    yield put(
      dealerStatisticsActions.fetchChatStatisticsSuccess({
        channels: channelResponse.content,
        nbSentMessages: internalMessagesResponse.content.find(({ visibility }) => visibility === PUBLIC)?.id,
        nbInternalMessages: internalMessagesResponse.content.find(({ visibility }) => visibility === PRIVATE)?.id,
        messagesDistribution: messagesDistributionResponse.content,
        messagesByIntervention: messagesByInterventionResponse.content,
        averagePerIntervention: messagesCountsResponse.content[0].messageCount,
        averageTimeToAnswer: averageTimeToAnswerResponse.content[0].responseTime,
        nbMediaFilesSharedWithCustomers: nbMediaFilesSharedWithCustomersResponse.content[0].publicMedia,
        nbMediaFileSharedInternally: nbMediaFileSharedInternallyResponse.content[0].internalMedia,
        averageNbMediaFilesSharedInChatPerFile: averageNbMediaFilesSharedInChatPerFileResponse.content[0].publicMedia,
        nbMediaFilesSentToCustomers: nbMediaFilesSentToCustomersResponse.content[0],
        nbMediaFilesReceivedToCustomers: nbMediaFilesReceivedToCustomersResponse.content[0],
      })
    );
  } catch (error) {
    yield put(dealerStatisticsActions.fetchChatStatisticsFailure(error));
  }
}

// `isFirstRendering` is useful for the reducers
function* filtersOnLocationChange(action) {
  const isFirstRendering = yield select(isFirstRenderingSelector);
  yield put(
    dealerStatisticsActions.locationChange(
      {
        ...action.payload,
        isFirstRendering,
      },
      action.meta
    )
  );
}

function* fetchSelfServiceStats({ payload }) {
  const { request } = payload;
  try {
    const { data } = yield call(restClient.request, request);

    // Fetch total data
    let totalStatsData = yield select(getSelfServiceDashboardStatsWTotal);
    if (request.params.page === 0) {
      const { page: _page, size: _size, sort: _sort, dealerId: _dealerId, reducer, ...totalRequestParams } = request.params;
      const reducers = reducer.filter(
        reducerName => !['dealerName,none', 'dealerId,none', 'country,none'].includes(reducerName)
      );
      const { data: totalData } = yield call(restClient.request, {
        ...request,
        params: { ...totalRequestParams, reducer: reducers },
      });
      const countries = uniq(data.content.map(({ country }) => country));
      const [totalContent] = totalData.content;
      totalStatsData = {
        ...totalContent,
        dealerId: 'all',
        dealerName: 'all',
        country: countries.length === 1 ? countries[0] : undefined,
      };
    }

    yield put(
      dealerStatisticsActions.fetchDashboardSelfServiceStatsSuccess({
        ...data,
        content: [...data.content, totalStatsData],
      })
    );
  } catch (error) {
    yield put(dealerStatisticsActions.fetchDashboardSelfServiceStatsFailure(error));
  }
}

function* makeRequest(request) {
  const data = yield* fetchPage(request);
  return data.content;
}

function* makeRequests(...requests) {
  const values = yield all(requests.map(request => call(makeRequest, request)));
  return values.flat();
}

function* fetchDetailsSelfServiceStats({ payload }) {
  const { requests, includedRequestTypes } = payload;

  try {
    const responseData = yield all(
      Object.entries(requests).reduce((acc, [requestType, request]) => {
        const params = Array.isArray(request) ? request : [request];
        return {
          ...acc,
          [requestType]: makeRequests(...params),
        };
      }, {})
    );
    yield put(dealerStatisticsActions.fetchDetailsSelfServiceStatsSuccess(responseData, { includedRequestTypes }));
  } catch (error) {
    yield put(dealerStatisticsActions.fetchDetailsSelfServiceStatsFailure(error, { includedRequestTypes }));
  }
}

function* refreshTab(tab, type, meta) {
  if (tab === SELF_SERVICE_STATS_TABS.SELF_SERVICE_STATUS) {
    const requestParams = yield select(getSelfServiceStatsDetailsFetchParams);
    yield put(dealerStatisticsActions.fetchDetailsSelfServiceStats(requestParams, meta?.refreshRequestTypes));
  }

  if (tab === SELF_SERVICE_STATS_TABS.SELF_SERVICE_TOP_CONTRIBUTORS) {
    const requestParams = yield select(getSelfServiceTopContributorsParams);
    yield put(dealerStatisticsActions.fetchSelfServiceTopContributorsStatsRequest(requestParams));
  }

  if (tab === SELF_SERVICE_STATS_TABS.SELF_SERVICE_QUESTIONS) {
    const params = yield select(getParams);
    if (type === types.SELECT_DEALER || type === SELECT_GROUP) {
      yield put(actions.clearQuestionModelStatistic());
    }
    if (params.dealerId) {
      yield put(
        actions.fetchAvailableQuestions(
          { ...params, projection: 'details', page: 0 },
          { source: 'questionSelfServiceStats' }
        )
      );
      yield call(fetchQuestionsDetails);
    }
  }

  if (tab === SELF_SERVICE_STATS_TABS.SELF_SERVICE_EXTRAS) {
    if (type === actionTypes.SET_SELECTED_SELF_SERVICE_TAB || type === types.STATISTICS_DEALER_DETAILS_READY) {
      const { page: _page, ...extraParams } = yield select(getExtraParams);
      const { originChecklistIds } = yield select(getExtraFilters);
      if (originChecklistIds.length > 0) {
        // Fetch already selected checklists
        yield put(
          searchExtras(
            {
              ...extraParams,
              ids: originChecklistIds,
              size: originChecklistIds.length,
              projection: 'page'
            },
            {
              alterable: false,
              source: 'extras-stats',
            }
          )
        );
        yield take([SEARCH_CHECKLISTS_SUCCESS, SEARCH_CHECKLISTS_FAILURE]);
      }
      // Fetch first page of checklists
      yield put(searchExtras({ ...extraParams, page: 0, projection: 'page' }, { alterable: false, source: 'extras-stats' }));
    }
    const params = yield select(getExtraDetailsParams);
    yield put(dealerStatisticsActions.fetchExtraDetailsStats({ ...params, page: 0 }));
  }

  if (tab === SELF_SERVICE_STATS_TABS.SELF_SERVICE_PROACTIVE_WORKS) {
    const params = yield select(getProactiveWorksDetailsParams);
    yield put(dealerStatisticsActions.fetchProactiveWorksDetailsStats({ ...params, page: 0 }));
  }
}

function* refreshSelfServiceStatsDetails(action) {
  const isInitialise = yield select(isInit);
  const isSelfServiceStatDetailsLocation = yield select(isSelfServiceStatsDetails);
  const tab = yield select(getSelfServiceTab);
  if (isInitialise && isSelfServiceStatDetailsLocation) {
    if (
      (action.type === types.SET_DEALER_STATISTICS_FILTERS && action.payload.countryCodes?.length > 0) ||
      action.type === SELECT_GROUP
    ) {
      yield take(types.SELECT_DEALER);
    }
    yield call(refreshTab, tab, action.type, action.meta);
  }
}

function* refreshSelfServiceDetailsTab({ payload, type }) {
  yield call(refreshTab, payload, type);
}

function* fetchExtraDetailsStats({ payload }) {
  const { request } = payload;
  try {
    const { data } = yield call(restClient.request, request);
    yield put(dealerStatisticsActions.fetchExtraDetailsStatsSuccess(data));
  } catch (error) {
    yield put(dealerStatisticsActions.fetchExtraDetailsStatsFailure(error));
  }
}

export default function* interventionStatisticsSagas() {
  yield takeLatest(LOCATION_CHANGE, filtersOnLocationChange);
  yield takeLatest([types.SET_DEALER_STATISTICS_FILTERS, types.SET_DEALER_STATISTICS_PERIOD], afterSetFiltersWorker);
  yield takeLatest(types.FETCH_STATISTICS_ANSWER_TIME_REQUEST, fetchAnswerTime);
  yield takeLatest(types.FETCH_STATISTICS_TIMELINE_STATUS_STATS_REQUEST, fetchStatusStats);
  yield takeLatest(types.FETCH_STATISTICS_DEALERS_REQUEST, fetchDealersStats);
  yield takeLatest(types.FETCH_STATISTICS_ALL_DEALERS_REQUEST, fetchAllDealersStats);
  yield takeLatest(types.FETCH_DEALERS_COUNTRIES_REQUEST, fetchCountriesStats);
  yield takeLatest(types.FETCH_STATISTICS_REVENUE_STATS_REQUEST, fetchRevenueStats);
  yield takeLatest(types.FETCH_STATISTICS_INTERVENTION_COUNT_REQUEST, fetchInterventionCount);
  yield takeLatest(types.FETCH_STATISTICS_INTERVENTION_BRANDS_REQUEST, fetchCurrentStatsBrands);
  yield takeLatest(types.FETCH_STATISTICS_INTERVENTION_STATUS_STATS_REQUEST, fetchCurrentStatusStats);
  yield takeLatest(types.FETCH_STATISTICS_REVENUE_BY_BRAND_STATS_REQUEST, fetchRevenueByBrandStats);
  yield takeLatest(types.FETCH_STATISTICS_TOP_CONTRIBUTORS_REQUEST, fetchTopContributorsStats);
  yield takeLatest(types.FETCH_CUSTOMER_OPINION_STATS_REQUEST, fetchCustomerOpinionStats);
  yield takeLatest(types.FETCH_STATISTICS_REVENUE_BY_PERIOD_STATS_REQUEST, fetchRevenueByPeriodStats);
  yield takeLatest(actionTypes.FETCH_CHAT_STATISTICS_REQUEST, fetchChatStatistics);
  yield takeLatest(types.FETCH_STATISTICS_INTERVENTION_RATING_STATS_REQUEST, fetchInterventionRatingStats);
  yield takeLatest(actionTypes.STATISTICS_LOCATION_CHANGE, startDashboardApplication);
  yield takeLatest(types.FETCH_LATEST_REFRESH_REQUEST, fetchLatestRefreshTime);
  yield takeLatest(actionTypes.FETCH_DASHBOARD_SELF_SERVICE_STATS, fetchSelfServiceStats);
  yield takeLatest(actionTypes.FETCH_DETAILS_SELF_SERVICE_STATS, fetchDetailsSelfServiceStats);
  yield takeLatest(
    [
      SELECT_GROUP,
      types.STATISTICS_DEALER_DETAILS_READY,
      types.SET_DEALER_STATISTICS_PERIOD,
      types.SET_DEALER_STATISTICS_FILTERS,
      actionTypes.SET_DETAILS_STATS_SELF_SERVICE_FILTERS,
      actionTypes.SET_EXTRA_FILTER,
      actionTypes.SET_PROACTIVE_WORKS_FILTER,
      actionTypes.SET_SELF_SERVICE_TOP_CONTRIBUTORS_FILTER,
    ],
    refreshSelfServiceStatsDetails
  );

  yield takeLatest(actionTypes.STATISTICS_LOCATION_CHANGE, initStatisticsDashboard);
  yield takeLatest(actionTypes.STATISTICS_LOCATION_CHANGE, initStatisticsDetails);
  yield takeLatest(types.SELECT_DEALER, onSelectedDealerChange);
  yield takeLatest(types.STATISTICS_DEALER_EXPORT, generateExportData);
  yield takeLatest(actionTypes.SET_SELECTED_SELF_SERVICE_TAB, refreshSelfServiceDetailsTab);
  yield takeLatest(actionTypes.FETCH_EXTRA_DETAILS_STATS, fetchExtraDetailsStats);
}
